import { useState } from 'react';
import {
  Box,
  FormControl,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
} from '@mui/material';
import AddLocationAltRoundedIcon from '@mui/icons-material/AddLocationAltRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '../../i18n';
import logoBlack from '../../assets/logo-black.png';
import { THEME_MODE_SELECTION_ENABLED } from '../../AppProviders';

const floatingSurface = {
  position: 'absolute',
  top: { xs: 'max(10px, env(safe-area-inset-top))', sm: 22 },
  minHeight: { xs: 44, sm: 48 },
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: { xs: '15px', sm: '17px' },
  bgcolor: 'background.paper',
  boxShadow: '0 10px 35px rgba(17,39,35,.12)',
  backdropFilter: 'blur(18px)',
};

export default function HeaderControls({
  language,
  mode,
  onLanguageChange,
  onToggleMode,
  onAddPlace,
  onAbout,
}) {
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const closeMenu = () => setMenuAnchor(null);
  const runMenuAction = (action) => {
    closeMenu();
    action?.();
  };

  return (
    <>
      <Paper
        component={Stack}
        direction="row"
        elevation={4}
        sx={{
          ...floatingSurface,
          insetInlineEnd: { xs: 10, sm: 24 },
          zIndex: 4,
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 1.2, sm: 1.5 },
          bgcolor: '#f7f9f8',
        }}
        aria-label="Calmpi"
      >
        <Box
          component="img"
          src={logoBlack}
          alt="Calmpi"
          sx={{
            display: 'block',
            width: { xs: 78, sm: 106 },
            height: 'auto',
            objectFit: 'contain',
          }}
        />
      </Paper>

      <Paper
        component={Stack}
        direction="row"
        elevation={4}
        sx={{
          ...floatingSurface,
          insetInlineStart: { xs: 10, sm: 24 },
          zIndex: 6,
          alignItems: 'center',
          gap: { xs: 0.25, sm: 1 },
          py: { xs: 0.2, sm: 0.6 },
          px: { xs: 0.5, sm: 0.9 },
          '& .MuiSelect-select': {
            minWidth: { xs: 44, sm: 72 },
            py: '5px !important',
            fontWeight: 750,
          },
        }}
      >
        <LanguageRoundedIcon sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'block' } }} />
        <FormControl size="small" variant="standard">
          <Select
            value={language}
            onChange={onLanguageChange}
            disableUnderline
            inputProps={{ 'aria-label': t('settings.language') }}
          >
            {SUPPORTED_LANGUAGES.map((code) => (
              <MenuItem key={code} value={code}>{t(`settings.languages.${code}`)}</MenuItem>
            ))}
          </Select>
        </FormControl>        <IconButton
          size="small"
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          aria-label={t('menu.open')}
          aria-controls={menuAnchor ? 'website-menu' : undefined}
          aria-expanded={Boolean(menuAnchor)}
          sx={{ width: 38, height: 38, color: 'text.secondary' }}
        >
          <MenuRoundedIcon />
        </IconButton>

        <Menu
          id="website-menu"
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          slotProps={{ paper: { sx: { mt: 1, minWidth: 190, borderRadius: 2 } } }}
        >
          <MenuItem onClick={() => runMenuAction(onAddPlace)}>
            <ListItemIcon><AddLocationAltRoundedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('menu.addPlace')}</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => runMenuAction(onAbout)}>
            <ListItemIcon><InfoOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('menu.about')}</ListItemText>
          </MenuItem>
        </Menu>
        {THEME_MODE_SELECTION_ENABLED && (
          <IconButton
            size="small"
            onClick={onToggleMode}
            aria-label={t(mode === 'dark' ? 'settings.light' : 'settings.dark')}
            sx={{ width: 38, height: 38, color: 'text.secondary' }}
          >
            {mode === 'dark' ? <LightModeRoundedIcon /> : <DarkModeRoundedIcon />}
          </IconButton>
        )}
      </Paper>
    </>
  );
}
